import { css, cx } from "@emotion/css";
import { BanIcon, CheckIcon, DashedCircleIcon, ExclamationCircleIcon, XmarkIcon, QuestionMarkIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import { exhaustiveCheck } from "@octopusdeploy/type-utils";
export const badgeStatuses = ["healthy", "healthy-with-warnings", "unavailable", "unhealthy", "unknown", "disabled"] as const;
export type BadgeStatus = (typeof badgeStatuses)[number];
type BadgeSize = "small" | "medium";
export interface StatusBadgeProps {
    /**
     * The status to display.
     * Determines the icon, label, and color scheme of the badge.
     */
    status: BadgeStatus;
    /**
     * Whether to show the text label alongside the icon.
     * @default false
     */
    showLabel?: boolean;
    size?: BadgeSize;
}
export function StatusBadge({ status, size = "medium", showLabel = false }: StatusBadgeProps) {
    const { icon: StatusIcon, label } = getIconAndLabel(status);
    return (<div className={cx(badgeStyles(size), statusStyles[status])}>
            <StatusIcon size={size === "small" ? 16 : 20} title={!showLabel ? label : undefined}/>
            {showLabel && size === "medium" && <span className={labelStyles}>{label}</span>}
        </div>);
}
function getIconAndLabel(status: BadgeStatus) {
    switch (status) {
        case "healthy":
            return { icon: CheckIcon, label: "Healthy" };
        case "healthy-with-warnings":
            return { icon: ExclamationCircleIcon, label: "Healthy with warnings" };
        case "unavailable":
            return { icon: BanIcon, label: "Unavailable" };
        case "unhealthy":
            return { icon: XmarkIcon, label: "Unhealthy" };
        case "unknown":
            return { icon: QuestionMarkIcon, label: "Unknown" };
        case "disabled":
            return { icon: DashedCircleIcon, label: "Disabled" };
        default:
            return exhaustiveCheck(status, `Unexpected status ${status} for StatusBadge`);
    }
}
const badgeStyles = (size: BadgeSize) => css({
    boxSizing: "border-box",
    display: "inline-flex",
    borderRadius: borderRadius.circle,
    alignItems: "center",
    justifyContent: "center",
    padding: size === "small" ? `${space[1]} ${space[1]}` : `${space[2]} ${space[2]}`,
    gap: space[4],
    font: text.body.regular.medium,
});
const labelStyles = css({
    paddingRight: space[4],
});
const statusStyles: Record<BadgeStatus, string> = {
    healthy: css({
        backgroundColor: themeTokens.color.statusBadge.background.healthy,
        color: themeTokens.color.statusBadge.text.primary,
        svg: {
            color: themeTokens.color.statusBadge.icon.default,
        },
    }),
    "healthy-with-warnings": css({
        backgroundColor: themeTokens.color.statusBadge.background.healthy,
        color: themeTokens.color.statusBadge.text.primary,
        svg: {
            color: themeTokens.color.statusBadge.icon.default,
        },
    }),
    unavailable: css({
        backgroundColor: themeTokens.color.statusBadge.background.warning,
        color: themeTokens.color.statusBadge.text.primary,
        svg: {
            color: themeTokens.color.statusBadge.icon.default,
        },
    }),
    unhealthy: css({
        backgroundColor: themeTokens.color.statusBadge.background.unhealthy,
        color: themeTokens.color.statusBadge.text.primary,
        svg: {
            color: themeTokens.color.statusBadge.icon.default,
        },
    }),
    unknown: css({
        backgroundColor: themeTokens.color.statusBadge.background.neutral,
        color: themeTokens.color.statusBadge.text.inverse,
        svg: {
            color: themeTokens.color.statusBadge.icon.inverse,
        },
    }),
    disabled: css({
        backgroundColor: themeTokens.color.statusBadge.background.neutral,
        color: themeTokens.color.statusBadge.text.inverse,
        svg: {
            color: themeTokens.color.statusBadge.icon.inverse,
        },
    }),
};
